import { useState, useCallback, useEffect } from 'react'
import { api } from '../../utils/api'
import {
  Wifi, WifiOff, Loader2, Globe, Key, Database,
  HelpCircle, ChevronDown, ChevronRight, ExternalLink,
  ShieldCheck, BookmarkCheck, Trash2, Save
} from 'lucide-react'
import toast from 'react-hot-toast'

interface Site {
  id: number
  name: string
  slug: string
  status: string
  racks_count: number
  devices_count: number
}

interface ConnectionStepProps {
  dcCode: string
  onConnected: (url: string, token: string, sites: Site[], defaultSiteId?: number | null) => void
  onSavedConnection?: () => void  // called when a saved connection is detected or saved
}

interface SavedConnection {
  saved: boolean
  netbox_url?: string
  token_hint?: string
  site_id?: number | null
  updated_at?: string
}

export function NetBoxConnectionStep({ dcCode, onConnected, onSavedConnection }: ConnectionStepProps) {
  const [url, setUrl] = useState('')
  const [token, setToken] = useState('')
  const [status, setStatus] = useState<'idle' | 'checking' | 'ok' | 'error'>('idle')
  const [sites, setSites] = useState<Site[]>([])
  const [selectedSiteId, setSelectedSiteId] = useState<number | null>(null)
  const [errorMsg, setErrorMsg] = useState('')
  const [showHelp, setShowHelp] = useState(false)
  const [savedConn, setSavedConn] = useState<SavedConnection | null>(null)
  const [loadingSaved, setLoadingSaved] = useState(false)
  const [saveChecked, setSaveChecked] = useState(true)
  const [deletingConn, setDeletingConn] = useState(false)

  // Auto-load saved connection for this DC
  useEffect(() => {
    if (!dcCode) return
    setLoadingSaved(true)
    api.get(`/api/integrations/netbox/connection?dc_code=${dcCode}`)
      .then(res => {
        setSavedConn(res.data)
        if (res.data.saved) onSavedConnection?.()
        if (res.data.saved && res.data.netbox_url) {
          setUrl(res.data.netbox_url)
          if (res.data.site_id) setSelectedSiteId(res.data.site_id)
          // Token stays empty — will be resolved server-side via use_saved=true
        }
      })
      .catch(() => setSavedConn({ saved: false }))
      .finally(() => setLoadingSaved(false))
  }, [dcCode])

  const checkConnection = useCallback(async () => {
    const trimUrl = url.trim()
    const trimToken = token.trim()
    if (!trimUrl || (!trimToken && !savedConn?.saved)) {
      toast.error('Укажите URL и токен')
      return
    }
    try {
      const parsed = new URL(trimUrl)
      if (!['http:', 'https:'].includes(parsed.protocol)) {
        toast.error('URL должен начинаться с http:// или https://')
        return
      }
    } catch {
      toast.error('Некорректный URL')
      return
    }
    setStatus('checking')
    setErrorMsg('')
    try {
      const res = await api.post(`/api/integrations/netbox/sites`, {
        url: trimUrl,
        token: trimToken || undefined,
        dc_code: dcCode,
        use_saved: true
      })
      setSites(res.data.sites || [])
      setStatus('ok')

      // Auto-save connection if checkbox is checked
      if (saveChecked && dcCode && trimToken) {
        try {
          await api.put('/api/integrations/netbox/connection', {
            dc_code: dcCode,
            url: trimUrl,
            token: trimToken,
            site_id: selectedSiteId ?? undefined,
          })
          setSavedConn({ saved: true, netbox_url: trimUrl, token_hint: `****${trimToken.slice(-4)}`, site_id: selectedSiteId })
          onSavedConnection?.()
          toast.success('Настройки подключения сохранены')
        } catch {
          // Non-critical — don't block the workflow
        }
      }
    } catch (e: any) {
      setStatus('error')
      const detail = e.response?.data?.detail || e.message || 'Не удалось подключиться к NetBox'
      setErrorMsg(detail)
    }
  }, [url, token, saveChecked, dcCode, savedConn, selectedSiteId])

  const deleteConnection = useCallback(async () => {
    if (!dcCode) return
    setDeletingConn(true)
    try {
      await api.delete(`/api/integrations/netbox/connection?dc_code=${dcCode}`)
      setSavedConn({ saved: false })
      setUrl('')
      setToken('')
      setStatus('idle')
      toast.success('Сохранённое подключение удалено')
    } catch {
      toast.error('Не удалось удалить подключение')
    } finally {
      setDeletingConn(false)
    }
  }, [dcCode])

  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-lg font-semibold text-text-primary mb-1">Подключение к NetBox</h2>
        <p className="text-sm text-text-secondary">
          Укажите адрес вашего NetBox и API-токен.
        </p>
      </div>

      {/* Saved connection badge */}
      {loadingSaved && (
        <div className="flex items-center gap-2 text-xs text-text-muted">
          <Loader2 className="w-3 h-3 animate-spin" />
          Загружаем сохранённые настройки...
        </div>
      )}

      {savedConn?.saved && !loadingSaved && (
        <div className="flex items-center gap-3 px-3 py-2 rounded-lg border border-green-500/30 bg-green-500/5">
          <BookmarkCheck className="w-4 h-4 text-green-400 shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-xs font-medium text-green-400">Сохранённое подключение</p>
            <p className="text-[11px] text-text-muted truncate">{savedConn.netbox_url} · токен {savedConn.token_hint}</p>
          </div>
          <button
            onClick={deleteConnection}
            disabled={deletingConn}
            className="shrink-0 p-1 text-text-muted hover:text-red-400 transition-colors"
            title="Удалить сохранённое подключение"
          >
            {deletingConn ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
          </button>
        </div>
      )}

      {/* Help accordion */}
      <button
        onClick={() => setShowHelp(v => !v)}
        className="w-full flex items-center gap-2 text-xs text-text-muted hover:text-accent-primary transition-colors py-2 px-3 rounded-lg border border-border bg-bg-panel"
      >
        <HelpCircle className="w-3.5 h-3.5 shrink-0" />
        <span className="flex-1 text-left font-medium">Как получить API токен NetBox?</span>
        {showHelp ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
      </button>

      {showHelp && (
        <div className="rounded-lg border border-border bg-bg-panel/50 p-4 space-y-2 text-xs text-text-secondary">
          <p className="font-medium text-text-primary">Шаги для получения токена в NetBox:</p>
          <ol className="list-decimal list-inside space-y-1.5 ml-1">
            <li>Войдите в NetBox с правами администратора</li>
            <li>Откройте меню <span className="font-mono bg-bg-input px-1 rounded">Admin → API Tokens</span></li>
            <li>Нажмите <span className="font-mono bg-bg-input px-1 rounded">+ Add</span></li>
            <li>Укажите пользователя, опционально — срок действия</li>
            <li>Нажмите <span className="font-mono bg-bg-input px-1 rounded">Save</span> и скопируйте токен</li>
          </ol>
          <div className="flex items-center gap-1.5 pt-1 text-green-400">
            <ShieldCheck className="w-3 h-3" />
            <span>Рекомендуем токен с правами только на чтение (read-only)</span>
          </div>
          <a
            href="https://docs.netbox.dev/en/stable/integrations/rest-api/#authentication"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1 text-accent-primary hover:underline"
          >
            <ExternalLink className="w-3 h-3" />
            NetBox API Authentication docs
          </a>
        </div>
      )}

      {/* Inputs */}
      <div className="space-y-4">
        <div>
          <label className="block text-xs font-medium text-text-secondary mb-1.5 uppercase tracking-wider">
            NetBox URL
          </label>
          <div className="relative">
            <Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted" />
            <input
              type="url"
              value={url}
              onChange={e => setUrl(e.target.value)}
              placeholder="https://netbox.company.com"
              autoComplete="off"
              className="w-full pl-9 pr-4 py-2.5 bg-bg-input border border-border rounded-lg text-sm text-text-primary placeholder:text-text-muted focus:outline-none focus:border-accent-primary focus:ring-1 focus:ring-accent-primary/20"
            />
          </div>
          <p className="text-[11px] text-text-muted mt-1">Например: http://netbox.local:8080 или https://netbox.example.com</p>
        </div>

        <div>
          <label className="block text-xs font-medium text-text-secondary mb-1.5 uppercase tracking-wider">
            API Token
          </label>
          <div className="relative">
            <Key className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted" />
            <input
              type="password"
              value={token}
              onChange={e => setToken(e.target.value)}
              placeholder={savedConn?.saved ? `Введите токен (сохранён: ${savedConn.token_hint})` : '40-символьный токен'}
              autoComplete="new-password"
              className="w-full pl-9 pr-4 py-2.5 bg-bg-input border border-border rounded-lg text-sm text-text-primary placeholder:text-text-muted focus:outline-none focus:border-accent-primary focus:ring-1 focus:ring-accent-primary/20 font-mono"
            />
          </div>
          <p className="text-[11px] text-text-muted mt-1 flex items-center gap-1">
            <ShieldCheck className="w-3 h-3 text-green-500" />
            Токен передаётся в теле запроса, не в URL
          </p>
        </div>

        {/* Save checkbox */}
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={saveChecked}
            onChange={e => setSaveChecked(e.target.checked)}
            className="w-4 h-4 rounded border-border accent-accent-primary"
          />
          <div className="flex items-center gap-1.5 text-xs text-text-secondary">
            <Save className="w-3.5 h-3.5" />
            Запомнить настройки для этого ЦОД (токен сохраняется зашифрованно)
          </div>
        </label>
      </div>

      {/* Test button */}
      <button
        onClick={checkConnection}
        disabled={status === 'checking' || !url.trim() || (!token.trim() && !savedConn?.saved)}
        className="w-full flex items-center justify-center gap-2 py-2.5 px-4 bg-accent-primary hover:bg-accent-primary/90 disabled:opacity-50 text-white rounded-lg text-sm font-medium transition-all"
      >
        {status === 'checking' ? (
          <><Loader2 className="w-4 h-4 animate-spin" /> Проверяем подключение...</>
        ) : (
          <><Wifi className="w-4 h-4" /> Проверить подключение</>
        )}
      </button>

      {/* Status result */}
      {status === 'ok' && (
        <div className="rounded-lg border border-green-500/30 bg-green-500/5 p-4">
          <div className="flex items-center gap-2 mb-3">
            <Wifi className="w-4 h-4 text-green-400" />
            <span className="text-sm font-medium text-green-400">NetBox доступен</span>
            <span className="ml-auto text-xs text-text-muted">{sites.length} сайтов</span>
          </div>
          <div className="space-y-1.5 max-h-48 overflow-y-auto">
            {sites.length === 0 && (
              <p className="text-xs text-text-muted text-center py-2">Сайты не найдены в NetBox</p>
            )}
            {sites.map(site => {
              const isSelected = selectedSiteId === site.id
              return (
                <div
                  key={site.id}
                  onClick={() => setSelectedSiteId(site.id)}
                  className={`flex items-center justify-between py-1.5 px-3 rounded-md text-sm cursor-pointer transition-colors ${
                    isSelected ? 'bg-accent-primary/20 border border-accent-primary' : 'bg-bg-panel/50 hover:bg-bg-panel border border-transparent'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <Database className={`w-3.5 h-3.5 ${isSelected ? 'text-accent-primary' : 'text-text-muted'}`} />
                    <span className="text-text-primary font-medium">{site.name}</span>
                    <span className="text-text-muted text-xs">({site.slug})</span>
                    {isSelected && (
                      <span className="text-[11px] bg-accent-primary/20 text-accent-primary px-1.5 py-0.5 rounded font-medium">
                        Выбрано
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-3 text-xs text-text-muted">
                    <span>{site.racks_count} стоек</span>
                    <span>{site.devices_count} устройств</span>
                  </div>
                </div>
              )
            })}
          </div>
          <button
            onClick={() => onConnected(url.trim(), token.trim(), sites, selectedSiteId)}
            className="mt-3 w-full py-2 px-4 bg-green-600 hover:bg-green-500 text-white rounded-lg text-sm font-medium transition-colors"
          >
            Продолжить →
          </button>
        </div>
      )}

      {status === 'error' && (
        <div className="rounded-lg border border-red-500/30 bg-red-500/5 p-4 flex items-start gap-3">
          <WifiOff className="w-4 h-4 text-red-400 shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm font-medium text-red-400">Нет подключения</p>
            <p className="text-xs text-text-muted mt-0.5">{errorMsg}</p>
            {(errorMsg?.includes('timed out') || errorMsg?.includes('connection')) && (
              <p className="text-xs text-yellow-400 mt-1.5">
                💡 Убедитесь что NetBox доступен по сети с сервера и порт открыт
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
